import axios from "../config/axios";
import { validateAudioFile, formatDuration, formatBytes } from "../utils/audioValidation";
import type { SpeechOptions } from "../components/medical/interfaces";

interface TranscriptionResponse {
  recordingId: number;
  transcription: string;
  notes: {
    subjective: string;
    objective: string;
    assessment: string;
    plan: string;
  };
}

interface ProcessParams {
  visit_type?: string;
  pronouns?: string;
  note_length?: string;
  patient_name?: string;
  is_image?: boolean;
  image_name?: string;
}

// Helper function to determine content type from filename extension
function getContentTypeFromFilename(filename: string): string {
  const extension = filename.split('.').pop()?.toLowerCase();
  switch (extension) {
    case 'jpg':
    case 'jpeg':
      return 'image/jpeg';
    case 'png':
      return 'image/png';
    case 'gif':
      return 'image/gif';
    case 'webp':
      return 'image/webp';
    case 'bmp':
      return 'image/bmp';
    case 'tiff':
      return 'image/tiff';
    case 'dicom':
    case 'dcm':
      return 'application/dicom';
    default:
      return 'image/jpeg'; // Default fallback
  }
}

export const speechService = {
  async getOptions(): Promise<SpeechOptions> {
    const response = await axios.get("/api/scribing/speech/options");
    return response.data;
  },

  async transcribeAudio(
    audioBlob: Blob,
    templateId: number,
    params?: ProcessParams
  ): Promise<TranscriptionResponse> {
    // Step 1: Get signed URL
    const timestamp = new Date().getTime();
    const isImage = params?.is_image || false;
    const filename = isImage 
      ? `image_${timestamp}_${params?.image_name || 'image.jpg'}`
      : `recording_${timestamp}.wav`;

    // Step 1.5: Validate audio file before requesting upload URL
    // Must happen before upload-url request, which creates a server-side recording entry
    if (!isImage) {
      const validation = await validateAudioFile(audioBlob);

      if (!validation.isValid) {
        console.error(`Audio validation failed: ${validation.warning}`);
        throw new Error(validation.warning || "Audio file validation failed. The file appears to be corrupted or too short.");
      }

      if (validation.warning) {
        console.warn(`Audio validation warning: ${validation.warning}`);
      }

      if (validation.duration > 0) {
        console.log(`Pre-upload audio validation: duration=${formatDuration(validation.duration)}, size=${formatBytes(validation.size)}`);
      }
    }

    const urlResponse = await axios.post(
      `/api/uploadprocessing/speech/upload-url?filename=${filename}`
    );

    const { uploadUrl, recordingId } = urlResponse.data;

    // Step 2: Upload audio or image to signed URL
    try {
      // Determine the correct Content-Type
      let contentType: string;

      if (isImage) {
        // For images, we need to ensure the Content-Type matches exactly what was used to generate the signed URL
        // First try to use the blob's type if available
        if (audioBlob.type && audioBlob.type.trim() !== '') {
          contentType = audioBlob.type;
        } 
        // Otherwise derive it from the filename
        else if (params?.image_name) {
          contentType = getContentTypeFromFilename(params.image_name);
        } 
        // Last resort fallback
        else {
          contentType = 'image/jpeg';
        }
      } else {
        // For audio, we'll use audio/wav
        contentType = 'audio/wav';
      }
      
      console.log(`Uploading to URL: ${uploadUrl}`);
      console.log(`Content-Type being used: ${contentType}`);
      console.log(`File is ${isImage ? 'an image' : 'audio'}, original filename: ${params?.image_name || 'not provided'}`);
      
      // Make the upload request with the determined Content-Type
      const uploadResponse = await fetch(uploadUrl, {
        method: "PUT",
        headers: {
          "Content-Type": contentType,
        },
        body: audioBlob,
      });

      if (!uploadResponse.ok) {
        throw new Error(`Upload failed with status ${uploadResponse.status}: ${uploadResponse.statusText}`);
      }

      console.log(`Upload completed: sent ${formatBytes(audioBlob.size)} to server (status: ${uploadResponse.status})`);
    } catch (error) {
      console.error("Upload error:", error);
      throw error;
    }

    // Step 3: Start processing with template ID and additional params
    const endpoint = isImage
      ? `/api/uploadprocessing/speech/process-image/${recordingId}/${templateId}`
      : `/api/uploadprocessing/speech/process/${recordingId}/${templateId}`;
      
    const processResponse = await axios.post(endpoint, params);
    return processResponse.data;
  },

  async generateNotes(
    recordingId: number,
    templateId: number,
    params?: ProcessParams
  ): Promise<TranscriptionResponse> {
    console.log("Calling generateNotes with:", {
      recordingId,
      templateId,
      params,
    });

    const response = await axios.post(
      `/api/scribing/speech/reprocess-with-template/${recordingId}/${templateId}`,
      {
        patient_name: params?.patient_name || "",
        visit_type: params?.visit_type || "",
        pronouns: params?.pronouns || "",
        note_length: params?.note_length || "",
      }
    );
    console.log("Generate notes response:", response.data);
    return response.data;
  },

  async getTranscriptionStatus(
    recordingId: number
  ): Promise<TranscriptionResponse> {
    const response = await axios.get(`/api/scribing/speech/status/${recordingId}`);
    return response.data;
  },

  async deleteRecording(recordingId: number): Promise<void> {
    await axios.delete(`/api/scribing/speech/recordings/${recordingId}`);
  },

  async updateSection(
    recordingId: number,
    sectionName: string,
    content: string
  ): Promise<void> {
    await axios.put(`/api/scribing/speech/recordings/${recordingId}/section`, {
      section_name: sectionName,
      content: content,
    });
  },

  async retryProcessing(
    recordingId: string,
    templateId: number | string
  ): Promise<{
    status: string;
    message: string;
    message_id: string;
    recording_id: string;
  }> {
    const response = await axios.post(
      `/api/uploadprocessing/speech/process/${recordingId}/${templateId}`
    );
    return response.data;
  },

  async retryProcess(
    recordingId: string,
    templateId: string,
    params?: {
      patient_name?: string;
      patient_id?: string;
      visit_type?: string;
      pronouns?: string;
      note_length?: string;
    }
  ): Promise<any> {
    // Build query parameters
    const queryParams = new URLSearchParams();
    if (params?.patient_name) queryParams.append('patient_name', params.patient_name);
    if (params?.patient_id) queryParams.append('patient_id', params.patient_id);
    if (params?.visit_type) queryParams.append('visit_type', params.visit_type);
    if (params?.pronouns) queryParams.append('pronouns', params.pronouns);
    if (params?.note_length) queryParams.append('note_length', params.note_length);
    if (templateId) queryParams.append('template_id', templateId);

    // Make the API call
    const response = await axios.post(
      `/api/uploadprocessing/speech/retry-process/${recordingId}?${queryParams.toString()}`
    );

    return response.data;
  },
};
